import { Link } from 'react-router-dom';

import { LegalPage, PageHero } from '../../components/Common';
import { Seo } from '../../components/Seo';
import {
  businessDetails,
  driverProduct,
  integrationGroups,
  pricing,
  pricingNotes,
  site,
  webApp,
} from '../../data/site';

/** The date the terms, refund, delivery and contact pages last changed. */
const COMMERCIAL_TERMS_UPDATED = '3 November 2026';

/** An address to write to, as a link. */
function Email({ to }: { to: string }) {
  return <a href={`mailto:${to}`}>{to}</a>;
}

/** The providers an integration group names, from the product metadata, as "A, B or C". */
function providersOf(title: string, exclude?: RegExp) {
  const items = (integrationGroups.find(group => group.title === title)?.items ?? []).filter(
    item => !exclude?.test(item),
  );
  return new Intl.ListFormat('en-GB', { type: 'disjunction' }).format(items);
}

export function Privacy() {
  const webAppHost = new URL(webApp.url).host;
  return (
    <>
      <Seo
        title="Privacy policy"
        description="How the Transport Book desktop app, web app, phone apps, and website handle your information."
      />
      <LegalPage
        title="Privacy policy"
        description="How the Transport Book desktop app, web app, phone apps, and website handle your information."
        updated={COMMERCIAL_TERMS_UPDATED}
      >
        <p>
          Transport Book is fleet and transport management software published by {site.publisher}{' '}
          (&ldquo;we&rdquo;, &ldquo;us&rdquo;). This policy covers the Transport Book desktop
          application for Windows and macOS, the web app at{' '}
          <a href={webApp.url} rel="noopener">
            {webAppHost}
          </a>
          , the Transport Book mobile application, the {driverProduct.name} application, the
          Transport Book server those apps work with, and this website. It explains what information
          the software handles, where it is kept, who else receives it, and the choices you have.
        </p>
        <p>
          We do not sell your information, we do not share it with advertisers, and we do not use
          your business records to train models or build advertising profiles.
        </p>

        <h2>Where your business data is kept</h2>
        <p>
          The operational records your organisation creates — trips, parties, vehicles, employees,
          job cards, inventory, accounts, transactions, and the details of attached documents — form
          your organisation&rsquo;s workspace. We host that workspace on the Transport Book server
          we operate. Each organisation&rsquo;s records are kept in a database of its own, separate
          from every other organisation&rsquo;s: for most organisations a database file on that
          server, and for some a managed database service (Turso) or a MongoDB database we operate.
          Sign-in accounts, organisation memberships, and a few indexes the server needs to find an
          organisation (the list of shared links, and which phone numbers belong to which
          organisation&rsquo;s drivers) are kept in a separate administrative MongoDB database.
        </p>
        <p>
          The <strong>desktop app</strong> is offline-first: it keeps a full copy of the workspace
          in a local database (SQLite) in your user profile on your own device, works without a
          connection, and synchronises with the workspace on our server when it can. If your
          organisation has instead entered a database of its own in the app&rsquo;s sync settings,
          the desktop synchronises with that database directly, and those records do not pass
          through our server.
        </p>
        <p>
          The <strong>web app</strong> works on the workspace held on our server. The browser keeps
          your sign-in for the open tab and a few preferences; it keeps no copy of your records
          between sessions.
        </p>
        <p>
          The <strong>mobile app</strong> reads the workspace through our server and, for
          organisations whose workspace we host with Turso, records changes there too — for example
          a job card, a payment received, or a trip update. When you share a bilty, an invoice, or a
          statement from the phone, our server prepares the document, the phone turns it into a PDF,
          and it goes to whichever app you pick in the share sheet.
        </p>
        <p>
          The <strong>{driverProduct.name}</strong> app is for drivers of organisations whose
          workspace we host with Turso. It shows the driver their assigned trip, the truck&rsquo;s
          document expiries, and their own account, and it sends our server the trip steps, photos
          (loading slips, bilty, PODs, receipts), expenses and advances, and vehicle problems the
          driver records. Money a driver reports waits for the office to approve it. Entries made
          without a connection are kept on the phone until they can be sent.
        </p>
        <p>
          We use the records in a workspace only to provide Transport Book to that organisation: to
          store and synchronise them, to run the features its users open, and to help when the
          organisation asks us to. To investigate a problem, we may copy an organisation&rsquo;s
          workspace into our own development environment.
        </p>

        <h2>Information we do receive</h2>
        <h3>Account and sign-in</h3>
        <p>
          Signing in, refreshing your session, and managing users in your organisation contact the
          Transport Book server. This handles your user name, organisation, role and membership, and
          the resulting session tokens. We use it only to authenticate you and to apply the
          permissions your organisation has granted. Which sessions have been signed out or revoked
          is kept in Upstash, a hosted Redis service, which the server also uses for short-lived
          cached results (for example, recent vehicle-tracking data).
        </p>
        <h3>Driver sign-in</h3>
        <p>
          A driver signs in to the {driverProduct.name} app with a mobile number that their
          organisation has entered and switched on. The one-time code is sent and checked by
          Firebase Authentication, a Google service, which therefore receives that phone number. The
          app then exchanges Firebase&rsquo;s confirmation for its own session with our server and
          does not use Firebase again until the driver signs out. A number the organisation has not
          switched on cannot sign in, and switching it off ends the driver&rsquo;s access.
        </p>
        <h3>Location</h3>
        <p>
          The {driverProduct.name} app shares the phone&rsquo;s location only when two things are
          both true: the organisation has switched on location tracking for that truck (it is off by
          default, and meant for a truck without a GPS unit), and the driver&rsquo;s trip is between
          loading and delivery. It then records the location in the background, at most about once a
          minute, and sends it to our server in batches. Tracking stops at delivery, at sign-out, or
          as soon as the organisation switches it off. The server keeps the truck&rsquo;s latest
          position and a compacted record of its route and stops with the organisation&rsquo;s
          records. On Android the app explains this before the system asks for permission, and a
          driver who refuses can still use everything else.
        </p>
        <p>
          The mobile app asks for your location only while it is open, to centre the live map and to
          place a geofence where you are standing.
        </p>
        <h3>Notifications</h3>
        <p>
          The phone apps register the phone for notifications with our server, which sends them
          through Expo&rsquo;s push service (Expo passes them to Google&rsquo;s or Apple&rsquo;s
          notification service). A notification is a short message: for a driver, a trip assigned, a
          status the office changed, or the office&rsquo;s decision on an expense; for office staff,
          approvals and a morning summary of what needs attention, such as the number of documents
          expiring. You can turn notifications off in your phone&rsquo;s settings.
        </p>
        <h3>Diagnostics and crash reports</h3>
        <p>
          The apps and our server send crash reports and a sampled share of performance traces to
          Sentry, so we can find and fix defects. A report contains the error, the stack trace, the
          app version and build revision, whether the copy was installed from a store or downloaded
          directly, and basic operating-system and device information. Reports from the phone apps
          also carry the internal identifiers of the signed-in account and its organisation and the
          account&rsquo;s role, so a fault can be traced to the right workspace; reports do not
          carry your name, phone number, or email address, or your trips, parties, financial
          records, or documents. Our server also sends its own logs, traces, and measurements to
          Grafana Cloud so we can operate it. The measurements — request counts, timings, error
          rates — never name your organisation, your account, or your records; the logs and traces
          describe the requests the server handled and can include the internal identifiers of the
          organisation and account involved.
        </p>
        <h3>Usage statistics</h3>
        <p>
          The desktop app, the web app, the two phone apps and this website collect usage statistics
          through Google Analytics. From the apps: which screens are opened, how often the app is
          launched and signed in to, the app version and the operating system, and, in the browser,
          how often the connection drops. The phone apps report through Firebase Analytics, which
          also records the phone model, how long the app is in use, when it was first opened or
          updated, and an approximate region worked out from the network address; advertising
          identifiers are never collected. From the website: page views, downloads and similar
          events, including when a shared statement is opened. Each install or browser is identified
          by a random number that is not derived from your device, your account or your
          organisation. An event names a screen or a page, never a record, a party, a file or
          anything you typed, and a shared statement&rsquo;s link is never sent.
        </p>

        <h2>Shared links</h2>
        <p>
          When someone in your organisation shares a statement as a link, a snapshot of that
          statement is stored on our server and shown to anyone who opens the link — after the
          password, if one was set, which we store only as a one-way hash. The snapshot does not
          change when your records do. A link stops working when it expires or is revoked, and the
          snapshot is deleted 30 days after that.
        </p>

        <h2>Services used only when your organisation turns them on</h2>
        <p>
          Several features connect to outside services. Each is optional, each is off until your
          organisation configures it, and each sends only what that feature needs to do its job.
          Credentials for these connections are encrypted before they are stored and are kept with
          your organisation&rsquo;s workspace, so its desktops, the web app, and the phones can use
          them; our server decrypts them only to carry out the feature on your behalf. When a
          feature is in use, that provider&rsquo;s own privacy policy also applies to the data it
          receives.
        </p>
        <ul>
          <li>
            <strong>Document storage</strong> — attachments are stored in the{' '}
            {providersOf('Document storage', /own computers/i)} your organisation connects. Our
            server reads a document from there to show it in the web app, and lets a phone upload a
            photo straight to it. We do not keep our own copy.
          </li>
          <li>
            <strong>Documents without cloud storage</strong> — an organisation that connects no
            storage keeps its documents on its own desktops, and a phone, a browser, or another
            computer receives them directly from one of those desktops. Our server only introduces
            the two devices. When they cannot connect directly, the encrypted transfer is relayed
            through Cloudflare&rsquo;s relay (TURN) service, which cannot read it.
          </li>
          <li>
            <strong>Maps, geocoding and routing</strong> — Google Maps and OpenStreetMap services
            receive the place names and coordinates needed to show a map, resolve an address, or
            draw a route.
          </li>
          <li>
            <strong>Vehicle tracking</strong> — our server fetches GPS positions from the tracking
            provider your organisation uses ({providersOf('Vehicle tracking')}), to show vehicles on
            the live map and follow trips.
          </li>
          <li>
            <strong>Fuel cards</strong> — {providersOf('Fuel cards')} fuel-card services, for
            balances, card limits, and transaction history on the cards you register. Our server
            makes these requests, or the desktop does where a provider does not accept them from the
            server.
          </li>
          <li>
            <strong>Email</strong> — an invoice, statement, or payment reminder sent by email goes
            through our server&rsquo;s email service. It is sent in your firm&rsquo;s name through
            Transport Book, replies go to your firm&rsquo;s address, and the message carries the
            recipient&rsquo;s address, the text, and any attached PDF.
          </li>
          <li>
            <strong>WhatsApp</strong> — messages are sent from your organisation&rsquo;s own
            WhatsApp Business account through Meta&rsquo;s WhatsApp Cloud API, using the message
            templates your account has had approved. Meta receives the recipient&rsquo;s number, the
            party and firm names, the document name and amount, and any attached PDF.
          </li>
        </ul>

        <h2>AI assistant</h2>
        <p>
          The desktop and web apps include an assistant that answers questions about your
          organisation&rsquo;s records and drafts work such as a trip or a payment follow-up for
          someone to check and save. When it is used, the question and the records it looks up to
          answer it are sent from our server to an AI provider. The app names the provider in use;
          we use only providers whose terms keep what is sent out of model training. The assistant
          sees only what the person asking is allowed to see, and it never saves anything on its
          own. Our server counts how many requests each organisation makes each day; it does not
          keep the conversations, which stay in the open window and end when it closes. An
          administrator can switch the assistant off for the organisation in Settings.
        </p>

        <h2>Support access</h2>
        <p>
          If you allow support access in Settings, each visit by Transport Book support while it is
          active is recorded.
        </p>

        <h2>Permissions the apps ask for</h2>
        <p>
          The desktop app needs local file access to keep its database and your attached documents,
          and network access for the features above. The mobile app asks for camera and photo access
          when you photograph or attach a document, location access while it is open as described
          above, and permission to show notifications. The {driverProduct.name} app asks for camera
          and photo access for trip photos and receipts, background location for trip tracking, and
          permission to show notifications. You can decline these and continue using the rest of the
          app.
        </p>

        <h2>Updates</h2>
        <p>
          Copies installed from the Microsoft Store are updated by the Store, under the settings you
          control there. Desktop copies downloaded from this website check our release feed for a
          newer version; that request carries the current version and platform, nothing more. The
          web app is updated when the page is reloaded. The phone apps download updates from
          Expo&rsquo;s update service, or are updated through the store or the package you installed
          them from.
        </p>

        <h2>Website</h2>
        <p>
          We process the technical information needed to serve and secure this website; its usage
          statistics are described above. If you submit a demo request, the details you enter are
          emailed to us through Brevo, an email delivery service, so we can reply; the website does
          not store them in a database. The site is hosted on Cloudflare Pages, and the demo form
          uses Firebase App Check, with Google reCAPTCHA, to block automated abuse. Installers and
          their release information are downloaded from Google Cloud Storage, through Firebase.
        </p>
        <p>
          If you start a free trial, we keep the details you enter — your business name, GSTIN if
          you give one, city and state, approximate fleet size, your name, email and mobile number —
          with your organisation&rsquo;s account on our server, so we can run your trial, contact
          you about it, and allow one free trial per business. Your mobile number is verified with a
          one-time SMS code sent by Firebase Authentication, which uses Google reCAPTCHA to block
          automated sign-ups. The username and password you choose become your sign-in; the password
          is stored only as a salted hash.
        </p>

        <h2>Paying for a plan</h2>
        <p>
          Payments are taken by Cashfree Payments India Private Limited. When you pay, your name,
          mobile number and email go to Cashfree so it can run the payment page, and your card, bank
          or UPI details are entered on that page and handled by Cashfree. We never see or store
          them. We keep what we need as a record: the amount, the plan, the date, the status and the
          receipt number, and we email the receipt to you.
        </p>
        <p>
          To work out what a plan costs, our server counts your organisation&rsquo;s own trucks and
          its hired trips a month. For a paid plan it records that number once a day and keeps each
          day&rsquo;s figure for about 100 days, since a renewal looks back over the last 90. It
          keeps the count and nothing about the trucks or trips themselves.
        </p>

        <h2>How long information is kept</h2>
        <p>
          Your organisation&rsquo;s records are kept for as long as your organisation keeps them in
          Transport Book; deleting a record in the app removes it from the workspace. An
          administrator can delete the whole organisation from Settings. Everyone is then signed
          out, the administrators can cancel for 30 days, and after that the organisation&rsquo;s
          data, including any older copy a move between databases left behind, is removed from our
          server for good. If a removal has to be finished by hand it can take longer. Our backups
          of it age out within about four days after that. A desktop that connects after the data is
          gone removes its own local copy: its database, the backups the app made, and the
          documents, exports and logs the app keeps in its own folders for that organisation. A
          folder you chose yourself stays. A computer keeps its copy until you remove it if it never
          connects, stays off for more than 90 days, or was not signed in to the organisation when
          the deletion was requested (signed out, or signed in to another organisation). Before
          deleting, an administrator can export every record from the same place. Shared-link
          snapshots are deleted 30 days after the link expires or is revoked. Diagnostic reports are
          deleted automatically by our error-reporting provider after its retention period.
          Demo-request emails are kept while we are in contact with you and deleted when they are no
          longer needed. Free-trial registration details are kept with your organisation&rsquo;s
          account, and deleted with it. Payment records and receipts are kept for as long as tax and
          accounting law requires, including after a workspace is deleted. When an organisation is
          deleted we also keep, for as long as we need to answer for what was done: our audit trail
          of changes made to the organisation and its accounts (who made them and when, never a
          password), a record that the organisation was deleted (its id, its short name and name,
          and the date), and the deletion request itself.
        </p>

        <h2>Security</h2>
        <p>
          Connections to our server and to third-party services use encrypted transport. Credentials
          for the connections listed above are encrypted before they are stored. Sign-in sessions
          expire and are renewed, and a renewal token presented a second time ends the whole
          session, so a copied token stops working. No system is perfectly secure, so keep your
          devices, your accounts, and any database or storage your organisation connects protected.
        </p>

        <h2>Children</h2>
        <p>
          Transport Book is business software. It is not directed at children and we do not
          knowingly collect information from anyone under 16.
        </p>

        <h2>Your rights</h2>
        <p>
          You may ask us what information we hold about you, ask for a copy, ask for it to be
          corrected, or ask us to delete it. Your organisation&rsquo;s records are its own, so
          requests about them are usually handled by your organisation, directly in the app; write
          to us and we will help. We respond within 30 days.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          If we change how the software handles information, we will update this page and move the
          date shown above. Continuing to use Transport Book after a change means the updated policy
          applies.
        </p>

        <h2>Contact</h2>
        <p>
          {site.publisher} — <Email to={businessDetails.emails.privacy} />.
        </p>
      </LegalPage>
    </>
  );
}
export function Terms() {
  return (
    <>
      <Seo
        title="Terms of service"
        description="The terms for using Transport Book, paying for a plan, and downloading the software."
      />
      <LegalPage
        title="Terms of service"
        description="The terms for using Transport Book, paying for a plan, and downloading the software."
        updated={COMMERCIAL_TERMS_UPDATED}
      >
        <p>
          These terms are between {businessDetails.legalName} (&ldquo;we&rdquo;, &ldquo;us&rdquo;),
          who publishes Transport Book, and the business that uses it (&ldquo;you&rdquo;). They
          cover the desktop, web and phone apps, the driver app and this website. By starting a
          trial, signing in or paying, you accept them. If you accept them for a business, you
          confirm that you may bind it. If the product or the website is renamed, these terms and
          any plan you have paid for carry on unchanged.
        </p>

        <h2>What Transport Book is</h2>
        <p>
          Transport Book is software for running a transport business: trips, documents, accounts,
          vehicles, drivers and reports. It is sold as a subscription for each organisation, and
          nothing physical is shipped (see <Link to="/delivery">Delivery</Link>).
        </p>

        <h2>Your account</h2>
        <p>
          You are responsible for who you give access to, what they do with it, and keeping
          passwords and devices safe. Tell us straight away if you think someone has got into your
          account. Each business may start one free trial.
        </p>

        <h2>Plans and prices</h2>
        <p>
          Prices are on the <Link to="/pricing">pricing page</Link> and are in Indian rupees. A plan
          is priced on your fleet size, which counts your own trucks. {pricingNotes.hiredTrips}{' '}
          {pricingNotes.perTruck} {pricingNotes.gst}
        </p>
        <p>{pricingNotes.trial}</p>
        <p>{pricingNotes.higherPlan}</p>
        <p>
          We may change our published prices. A change never alters a period you have already paid
          for. Your per-truck rate does not rise for as long as you stay subscribed, though what you
          pay still follows your fleet size, and GST is added if the law comes to require it.
        </p>

        <h2>Paying</h2>
        <p>
          An organisation&rsquo;s administrator pays through a payment link, which stays open for{' '}
          {pricing.paymentLinkDays} days. Payments are taken by Cashfree Payments India Private
          Limited, and the plan starts once Cashfree confirms the whole amount. We email a receipt.
          Monthly billing, quotes for large fleets and other arrangements are agreed with us
          directly.
        </p>

        <h2>Renewal and what happens if you do not renew</h2>
        <p>
          <strong>We never charge you automatically.</strong> A plan runs for the year you paid for
          and renews only if you pay again. We remind you{' '}
          {pricing.renewalReminderDays.join(', ').replace(/, (\d+)$/, ' and $1')} days before it
          ends, and you may renew from {pricing.renewalReminderDays[0]} days before. Renewing early
          loses you no days.
        </p>
        <p>
          After the end date everything keeps working for {pricing.renewalGraceDays} days. After
          that, a fleet within the Free limit moves to Free, and a larger one becomes read-only: you
          can still sign in, read and export your records, but not add or change them, until you
          renew. We do not delete your records because a plan has ended.
        </p>

        <h2>If your fleet grows</h2>
        <p>
          {pricingNotes.growth} A renewal is priced on the largest fleet size you held for at least
          7 days in the last 90.
        </p>

        <h2>Refunds</h2>
        <p>
          You may ask for your money back within {pricing.refundDays} days of paying, with no
          questions asked. The <Link to="/refunds">refund policy</Link> has the details.
        </p>

        <h2>Your data</h2>
        <p>
          Your organisation&rsquo;s records belong to it. We use them only to run the service for
          you, as the <Link to="/privacy">privacy policy</Link> describes. You can export them
          whether or not you are paying. If you stop using Transport Book, an administrator can
          delete the organisation from Settings. Everyone is signed out, an administrator can cancel
          within 30 days, and then the data is removed from our server (if a removal has to be
          finished by hand it can take longer) and our backups age out within about four days.
          Payment records are kept as tax law requires. A desktop removes its local copy (its
          database, the backups the app made and the documents, exports and logs it keeps in its own
          folders) the next time it connects after that; a folder you chose yourself stays. A
          computer keeps its copy if it never connects, stays off for more than 90 days, or was not
          signed in to the organisation when the deletion was requested (signed out, or signed in to
          another organisation). If you cannot do this yourself, write to us.
        </p>

        <h2>Fair use</h2>
        <p>
          Do not use Transport Book to break the law, to store records you have no right to hold, to
          get into another organisation&rsquo;s data, to overload or attack the service, or to
          resell it without our agreement.
        </p>

        <h2>Your responsibility for your figures and filings</h2>
        <p>
          Transport Book helps you keep records and prepare documents. It does not replace an
          accountant or a tax adviser, and it does not give legal or tax advice. You are responsible
          for checking your entries, invoices, e-way bills and GST and other filings before you rely
          on them. Suggestions and drafts, including those from the AI assistant, are proposals for
          you to check.
        </p>

        <h2>Services run by others</h2>
        <p>
          Some features connect to services you choose, such as GPS providers, fuel cards, banks,
          FASTag and government portals, email and WhatsApp, and cloud storage, often with your own
          login. Your agreement with each of them applies, and we are not responsible for what they
          do or for their being unavailable.
        </p>

        <h2>Availability</h2>
        <p>
          We work to keep Transport Book running and your data backed up, but we do not promise it
          will be uninterrupted or free of errors. Keep your own exports of anything you could not
          do without. Website content describes the product in general and is not a certification or
          a promise of a particular integration.
        </p>

        <h2>Suspending or ending access</h2>
        <p>
          You may stop using Transport Book at any time. We may suspend or end an account that
          breaks these terms or puts the service or other customers at risk, or where the law
          requires it. Unless the law or the risk prevents it, we will tell you first and give you
          the chance to export your records.
        </p>

        <h2>Our software</h2>
        <p>
          We own Transport Book and everything in it apart from your records. You may use it while
          these terms apply, but may not copy, reverse engineer or resell it. Install it only from
          our official pages, and verify the checksums they show.
        </p>

        <h2>Limits on our liability</h2>
        <p>
          As far as the law allows, we are not liable for indirect or consequential loss, such as
          lost profit, lost business, or tax penalties and interest. Our total liability for any
          claim is limited to what you paid us in the 12 months before it arose. Nothing here limits
          a right or a liability that the law does not allow us to limit.
        </p>

        <h2>Changes to these terms</h2>
        <p>
          If we change these terms we will update this page and move the date above, and tell you in
          advance of any change that matters to what you have paid for. Continuing to use Transport
          Book after a change means you accept it.
        </p>

        <h2>Law and disputes</h2>
        <p>
          These terms are governed by the laws of India. Please{' '}
          <Link to="/contact">write to us</Link> first, as most problems can be settled that way.
          Any dispute we cannot settle goes to the courts at the place of{' '}
          {businessDetails.legalName}&rsquo;s registered office.
        </p>
      </LegalPage>
    </>
  );
}

export function Refunds() {
  return (
    <>
      <Seo
        title="Refund and cancellation policy"
        description={`Ask within ${pricing.refundDays} days of paying and we refund you. Nothing renews automatically.`}
      />
      <LegalPage
        title="Refund and cancellation policy"
        description={`Ask within ${pricing.refundDays} days of paying and we refund you. Nothing renews automatically.`}
        updated={COMMERCIAL_TERMS_UPDATED}
      >
        <h2>Refunds within {pricing.refundDays} days</h2>
        <p>
          If you ask within {pricing.refundDays} days of a payment, we refund the whole of it, with
          no questions asked. This covers a yearly payment, a renewal and a payment for fleet
          growth.
        </p>

        <h2>How to ask</h2>
        <p>
          Email <Email to={businessDetails.emails.billing} /> with your organisation&rsquo;s name
          and your receipt number.
        </p>

        <h2>How and when the money comes back</h2>
        <p>
          We send the money back to the card, bank account or UPI ID you paid from, through Cashfree
          Payments, the company that took the payment. We start it within 3 working days of your
          email, and your bank usually shows it within 5 to 7 working days after that.
        </p>

        <h2>What happens to your plan</h2>
        <p>
          The period you were refunded for ends when the refund is made. A fleet within the Free
          limit moves to Free, and a larger one becomes read-only. Your records are not deleted, and
          you can still sign in, read and export them. If you were refunded for fleet growth, your
          plan goes back to the fleet size you had paid for before.
        </p>

        <h2>After {pricing.refundDays} days</h2>
        <p>
          Payments are not refunded after {pricing.refundDays} days, except where we made a mistake
          or the law requires it. Your plan keeps running until its end date.
        </p>

        <h2>Paid twice or charged the wrong amount</h2>
        <p>
          If you were charged twice or the wrong amount, write to us at any time and we will refund
          what you should not have paid. Money that does not match your plan is flagged to us
          automatically, and we will contact you about it. If money left your account but no payment
          shows in the app, your bank usually returns it by itself within a few working days; if it
          does not, write to us.
        </p>

        <h2>Cancelling</h2>
        <p>
          Nothing renews automatically, so there is nothing to cancel to stop being charged: if you
          do not pay again, the plan ends as the <Link to="/terms">terms</Link> describe. A payment
          link you have not paid simply expires after {pricing.paymentLinkDays} days and costs
          nothing.
        </p>
        <p>
          To delete the organisation, an administrator uses Settings in the app. We do not refund
          the paid time left automatically; this refund policy applies, so ask within{' '}
          {pricing.refundDays} days of paying if you want your money back. Deleting is scheduled:
          everyone is signed out, an administrator can cancel within 30 days, and then the data is
          removed. Payment records are kept as tax law requires.
        </p>
      </LegalPage>
    </>
  );
}

export function Delivery() {
  return (
    <>
      <Seo
        title="Delivery policy"
        description="Transport Book is software, so a plan is switched on online and nothing is shipped."
      />
      <LegalPage
        title="Delivery policy"
        description="Transport Book is software, so a plan is switched on online and nothing is shipped."
        updated={COMMERCIAL_TERMS_UPDATED}
      >
        <h2>Nothing is shipped</h2>
        <p>
          Transport Book is software used online and through its apps. We send no physical goods, so
          there are no shipping charges and no delivery address.
        </p>

        <h2>When your plan starts</h2>
        <p>
          Your plan is switched on for your organisation as soon as Cashfree Payments confirms your
          payment, usually within a minute. The app updates by itself and the receipt goes to your
          email. A free trial starts as soon as you sign up.
        </p>

        <h2>Getting the apps</h2>
        <p>
          The web app opens in your browser. The desktop, Android and driver apps are downloaded
          from our <Link to="/downloads/desktop">download pages</Link> or the store that lists them.
        </p>

        <h2>If your plan has not started</h2>
        <p>
          If you have paid and your plan has not changed within 24 hours, email{' '}
          <Email to={businessDetails.emails.billing} /> with your organisation&rsquo;s name and the
          payment details, and we will sort it out.
        </p>
      </LegalPage>
    </>
  );
}

const CONTACT_WAYS = [
  {
    title: 'Help with the app',
    email: businessDetails.emails.support,
    body: 'Installing, signing in, or something that is not working.',
  },
  {
    title: 'Payments and refunds',
    email: businessDetails.emails.billing,
    body: 'Include your organisation’s name and your receipt number.',
  },
  {
    title: 'General questions',
    email: businessDetails.emails.general,
    body: 'Anything else about Transport Book.',
  },
  {
    title: 'Privacy and your data',
    email: businessDetails.emails.privacy,
    body: 'What we hold about you, and asking for a copy or to delete it.',
  },
  {
    title: 'Complaints',
    email: businessDetails.emails.grievance,
    body: 'We acknowledge within 48 hours and aim to resolve within one month.',
  },
];

export function Contact() {
  return (
    <>
      <Seo
        title="Contact us"
        description={`How to reach ${businessDetails.legalName}, who publishes Transport Book.`}
      />
      <PageHero
        compact
        description={`Write to whichever address fits and it reaches the right person. Transport Book is published by ${businessDetails.legalName}.`}
        eyebrow="Contact"
        title="Contact us"
      />
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {CONTACT_WAYS.map(way => (
              <article className="card" key={way.email}>
                <h2 className="card-title">{way.title}</h2>
                <p>{way.body}</p>
                <p className="contact-email">
                  <Email to={way.email} />
                </p>
              </article>
            ))}
            <article className="card">
              <h2 className="card-title">Not sure yet?</h2>
              <p>
                Most questions are answered in <Link to="/support">support</Link>, or you can{' '}
                <Link to="/request-demo">request a demo</Link> and see the app first.
              </p>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}

export function Cookies() {
  return (
    <>
      <Seo title="Cookie policy" description="Analytics and browser storage on this site." />
      <LegalPage title="Cookie policy" description="Analytics and browser storage on this site.">
        <h2>Essential storage</h2>
        <p>
          The site stores your light or dark theme if you switch it. Security services may use
          browser data to prevent abuse.
        </p>
        <h2>Analytics</h2>
        <p>
          Google Analytics measures page views, feature navigation, demo events, platform selection,
          and downloads, so we can see which pages and downloads are useful. IP addresses are
          anonymised and advertising storage is disabled.
        </p>
      </LegalPage>
    </>
  );
}
